import {
  getReunions,
  getReunionComplete,
  createReunion,
  addAgendaItem,
  addNote,
  addAction,
  saveResume,
  genererResume,
  type ComiteReunion,
  type ComiteReunionComplete,
} from './comiteService';

const API_BASES = ['/api/serq/tech-primes', '/api/prisme/tech-primes'];
const TECH_PRIMES_SCOPE = 'TECH_PRIMES';

export interface TechPrimesWorkbookTabRow {
  id: string;
  cells: string[];
}

export interface TechPrimesWorkbookTab {
  name: string;
  maxCols: number;
  headerRow: string[];
  subHeaderRow: string[];
  rows: TechPrimesWorkbookTabRow[];
}

export interface TechPrimesWorkbook {
  version: number;
  tabsOrder: string[];
  tabs: Record<string, TechPrimesWorkbookTab>;
}

export async function getTechPrimesReunions(clientId: number): Promise<ComiteReunion[]> {
  return getReunions(clientId, TECH_PRIMES_SCOPE);
}

export async function getTechPrimesReunionComplete(reunionId: number): Promise<ComiteReunionComplete> {
  return getReunionComplete(reunionId);
}

export async function createTechPrimesReunion(data: Partial<ComiteReunion>) {
  return createReunion({
    ...data,
    comiteScope: TECH_PRIMES_SCOPE,
  });
}

export async function addTechPrimesAgendaItem(payload: Parameters<typeof addAgendaItem>[0]) {
  return addAgendaItem(payload);
}

export async function addTechPrimesNote(payload: Parameters<typeof addNote>[0]) {
  return addNote(payload);
}

export async function addTechPrimesAction(payload: Parameters<typeof addAction>[0]) {
  return addAction(payload);
}

export async function generateTechPrimesResume(reunionId: number, notes: string, decisions: string, enjeux: string, actions: string) {
  return genererResume(reunionId, notes, decisions, enjeux, actions);
}

export async function saveTechPrimesResume(reunionId: number, text: string, generatedByAI: boolean) {
  return saveResume(reunionId, text, generatedByAI);
}

export async function getTechPrimesWorkbook(clientId: number): Promise<{ workbook: TechPrimesWorkbook; source_file_name?: string | null }> {
  let lastError: Error | null = null;
  for (const base of API_BASES) {
    const res = await fetch(`${base}/workbook/${clientId}`, { credentials: 'include' });
    if (res.ok) return await res.json();
    if (res.status === 404) {
      lastError = new Error('Route API non trouvée (tech-primes/workbook)');
      continue;
    }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur chargement workbook Techniciens Primes');
  }
  throw lastError || new Error('Erreur chargement workbook Techniciens Primes');
}

export async function saveTechPrimesWorkbook(clientId: number, workbook: TechPrimesWorkbook, sourceFileName?: string | null) {
  let lastError: Error | null = null;
  for (const base of API_BASES) {
    const res = await fetch(`${base}/workbook/${clientId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ workbook, source_file_name: sourceFileName ?? null }),
    });
    if (res.ok) return await res.json();
    if (res.status === 404) {
      lastError = new Error('Route API non trouvée (save workbook)');
      continue;
    }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur sauvegarde workbook Techniciens Primes');
  }
  throw lastError || new Error('Erreur sauvegarde workbook Techniciens Primes');
}

export async function importTechPrimesWorkbookXlsx(clientId: number, file: File) {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  const fileContentBase64 = btoa(binary);

  let lastError: Error | null = null;
  for (const base of API_BASES) {
    const res = await fetch(`${base}/workbook/import-xlsx`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        clientId,
        fileName: file.name,
        fileContentBase64,
      }),
    });
    if (res.ok) return await res.json();
    if (res.status === 404) {
      lastError = new Error('Route API non trouvée (import workbook)');
      continue;
    }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur import XLSX Techniciens Primes');
  }
  throw lastError || new Error('Erreur import XLSX Techniciens Primes');
}

export async function exportTechPrimesWorkbookXlsx(clientId: number): Promise<Blob> {
  let lastError: Error | null = null;
  for (const base of API_BASES) {
    const res = await fetch(`${base}/workbook/${clientId}/export-xlsx`, {
      credentials: 'include',
    });
    if (res.ok) return await res.blob();
    if (res.status === 404) {
      lastError = new Error('Route API non trouvée (export workbook)');
      continue;
    }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur export XLSX Techniciens Primes');
  }
  throw lastError || new Error('Erreur export XLSX Techniciens Primes');
}
